import { Notification } from '@carbon/icons-react';
import type { ElementType } from 'react';
import { CdLink } from '../../atoms/CdLink';
import { CdEmptyState } from '../../molecules/CdEmptyState';
import { CdFeedItem, type CdFeedItemProps } from '../../molecules/CdFeedItem';
import { cx } from '../../utils';
import { CdSidePanel } from '../CdSidePanel';
import styles from './CdNotificationsPanel.module.scss';

export type NotificationFilter = 'all' | 'unread';

export interface CdNotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  items: Omit<CdFeedItemProps, 'linkAs'>[];
  filter: NotificationFilter;
  onFilterChange: (filter: NotificationFilter) => void;
  seeAllHref?: string;
  linkAs?: ElementType;
  labels?: {
    title?: string;
    all?: string;
    unread?: string;
    seeAll?: string;
    emptyTitle?: string;
    emptyDescription?: string;
  };
}

export const CdNotificationsPanel = ({
  open,
  onClose,
  items,
  filter,
  onFilterChange,
  seeAllHref,
  linkAs,
  labels = {},
}: CdNotificationsPanelProps) => {
  const visible = filter === 'unread' ? items.filter((i) => i.unread) : items;

  const filterButton = (value: NotificationFilter, label: string) => (
    <button
      type="button"
      className={cx(styles.filter, filter === value && styles.filterActive)}
      aria-pressed={filter === value}
      onClick={() => onFilterChange(value)}
    >
      {label}
    </button>
  );

  return (
    <CdSidePanel
      open={open}
      onClose={onClose}
      title={labels.title ?? 'Notifications'}
      closeLabel="Close notifications"
      headerActions={
        <div className={styles.filters} role="group" aria-label="Filter notifications">
          {filterButton('all', labels.all ?? 'All')}
          {filterButton('unread', labels.unread ?? 'Unread')}
        </div>
      }
    >
      {visible.length === 0 ? (
        <CdEmptyState
          icon={Notification}
          size="page"
          title={labels.emptyTitle ?? 'No new notifications'}
          description={labels.emptyDescription ?? "You're all caught up."}
          className={styles.empty}
        />
      ) : (
        <>
          <ul className={styles.list}>
            {visible.map((item, index) => (
              <li key={item.id}>
                <CdFeedItem {...item} highlighted={index === 0 && item.unread} linkAs={linkAs} />
              </li>
            ))}
          </ul>
          {seeAllHref && (
            <div className={styles.seeAll}>
              <CdLink as={linkAs} href={seeAllHref} onClick={onClose}>
                {labels.seeAll ?? 'See all'}
              </CdLink>
            </div>
          )}
        </>
      )}
    </CdSidePanel>
  );
};
